import { useEffect, useRef } from "react";

const industries = [
  {
    icon: "ri-funds-line",
    title: "Financial Advisory & Insurance",
    desc: "Capture, qualify, and convert advisory leads with automated intake and compliance-ready documentation workflows.",
  },
  {
    icon: "ri-home-2-line",
    title: "Mortgage & Lending",
    desc: "Accelerate the loan pipeline from inquiry to approval with intelligent workflow systems and underwriting automation.",
  },
  {
    icon: "ri-cpu-line",
    title: "Fintech Platforms",
    desc: "Scale customer acquisition and onboarding with AI-powered qualification layers and deep CRM integration.",
  },
  {
    icon: "ri-building-4-line",
    title: "Real Estate & Capital Markets",
    desc: "Unify deal flow, investor communications, and reporting into a single operational intelligence system.",
  },
  {
    icon: "ri-community-line",
    title: "Property Operations & Development",
    desc: "Manage tenant acquisition, vendor workflows, and asset reporting with streamlined automation systems.",
  },
];

export default function IndustriesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll<HTMLElement>("[data-reveal]");
            els.forEach((el, i) => {
              setTimeout(() => {
                el.style.opacity = "1";
                el.style.transform = "translateY(0)";
              }, i * 80);
            });
          }
        });
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="industries" className="relative bg-bfx-graphite overflow-hidden" ref={sectionRef}>
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/24e8e10b080b2db9c6c8e805ab20860a.jpg"
          alt=""
          className="w-full h-full object-cover object-center opacity-20"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-bfx-graphite via-bfx-graphite/90 to-bfx-graphite pointer-events-none" />
      <div className="absolute inset-0 grid-bg-dark opacity-50 pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-bfx-accent/25 to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 py-32">
        {/* Centered header */}
        <div className="text-center mb-20">
          <p
            data-reveal
            className="text-[11px] font-semibold uppercase tracking-[0.14em] text-bfx-accent mb-4"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            Industries
          </p>
          <h2
            data-reveal
            className="text-4xl md:text-5xl lg:text-[58px] font-semibold text-white leading-[1.05] tracking-tight mb-5"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            Built for Financial Operators.
          </h2>
          <p
            data-reveal
            className="text-base text-white/50 max-w-md mx-auto leading-relaxed"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            Purpose-built intelligence for sectors where speed, compliance, and conversion determine outcomes.
          </p>
        </div>

        {/* Industry grid — Apple-like glassmorphism tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {industries.map((ind) => (
            <div
              key={ind.title}
              data-reveal
              className="group relative rounded-2xl p-8 border border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.08] hover:border-bfx-accent/30 transition-all duration-400 cursor-default backdrop-blur-sm overflow-hidden"
              style={{ opacity: 0, transform: "translateY(20px)" }}
            >
              {/* Hover glow */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-bfx-accent/0 to-transparent group-hover:via-bfx-accent/30 transition-all duration-300" />

              <div className="w-10 h-10 flex items-center justify-center text-bfx-accent mb-6">
                <i className={`${ind.icon} text-2xl`} />
              </div>
              <h3 className="text-base font-semibold text-white mb-3 leading-snug">
                {ind.title}
              </h3>
              <p className="text-sm text-white/45 leading-relaxed">{ind.desc}</p>
            </div>
          ))}

          {/* CTA tile */}
          <div
            data-reveal
            className="group rounded-2xl p-8 border border-bfx-accent/20 bg-bfx-accent/[0.06] hover:bg-bfx-accent/[0.12] transition-all duration-300 cursor-pointer flex flex-col justify-between"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            <div>
              <div className="w-10 h-10 flex items-center justify-center text-bfx-accent mb-6">
                <i className="ri-add-circle-line text-2xl" />
              </div>
              <h3 className="text-base font-semibold text-white mb-3">
                Not in this list?
              </h3>
              <p className="text-sm text-white/45 leading-relaxed">
                We work with any operator managing complex financial pipelines and high-value inbound flow.
              </p>
            </div>
            <div className="flex items-center gap-2 mt-8 text-bfx-accent text-xs font-semibold">
              <span>Talk to us</span>
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}